/**
 * Authentication hooks for API integration
 * 
 * Provides React Query hooks for login, logout, and token refresh.
 * Integrates with Zustand auth store for state management.
 * 
 * Sprint 31.9 Phase 11: Enhanced for Identity Center session model.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { storeRefreshToken } from '../lib/tokenRefresh';
import { consumeReturnPath } from '../lib/returnPath';
import { useNavigate } from 'react-router-dom';
import { getErrorMessage, setAuthToken } from '../api-client';
import { useAuthStore, SessionOption, SessionContext } from '../stores/authStore';

// Query keys for cache management
export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const,
  session: () => [...authKeys.all, 'session'] as const,
};

/**
 * Parse JWT payload to extract user claims
 * 
 * @param token - JWT access token
 * @returns Decoded payload object
 */
function parseJwtPayload(token: string): Record<string, unknown> {
  try {
    const base64 = token.split('.')[1];
    if (!base64) throw new Error('Invalid token format');
    const payload = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload) as Record<string, unknown>;
  } catch {
    throw new Error('Failed to parse token');
  }
}

/**
 * API response types for login flow
 */
interface LoginApiResponse {
  access_token?: string;
  refresh_token?: string;
  pending_token?: string;
  session_options?: SessionOption[];
  user_id: string;
  username: string;
  requires_session_selection: boolean;
  // Sprint 41 Phase 3.1: MFA challenge fields
  mfa_required?: boolean;
  mfa_token?: string;
  mfa_enrolled?: boolean;
}

interface SessionEstablishResponse {
  access_token: string;
  refresh_token: string;
}

/**
 * Hook for user login
 * 
 * Sprint 31.9 Phase 11: Multi-step login flow
 * - If single session option: auto-selects and returns tokens
 * - If multiple options: returns pending_token for session selection
 */
export function useLogin() {
  const { login, setPendingLogin, setMfaPending } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, password, deviceFingerprint }: { email: string; password: string; deviceFingerprint?: string }) => {
      // Sprint 31.10: Use login/v2 endpoint for Identity Center flow with session selection
      const response = await fetch('/api/v1/auth/login/v2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: email,
          password,
          // Sprint 41 Phase 3.1: Send device fingerprint for trusted device check
          ...(deviceFingerprint ? { device_fingerprint: deviceFingerprint } : {}),
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(getErrorMessage(error) || 'Invalid credentials');
      }

      const data = await response.json() as LoginApiResponse;
      return { ...data, email };
    },
    onSuccess: async (data) => {
      // Clear any cached data from previous session
      queryClient.clear();

      // Sprint 41 Phase 3.1: Check if MFA challenge is required
      if (data.mfa_required && data.mfa_token) {
        setMfaPending(data.mfa_token, data.mfa_enrolled ?? false, data.user_id, data.email);
        navigate('/mfa-challenge');
        return;
      }

      if (data.requires_session_selection && data.pending_token && data.session_options) {
        // Multiple sessions available - need user to select
        setPendingLogin(
          data.pending_token,
          data.session_options,
          data.user_id,
          data.email
        );
        // Navigate to session selection page
        navigate('/select-session');
      } else if (data.access_token) {
        // Single session auto-selected - complete login
        await completeLogin(data.access_token, login);
        navigate(consumeReturnPath() ?? '/');
      }
    },
  });
}

/**
 * Hook for establishing session after selection
 * 
 * Sprint 31.9 Phase 11: Called when user selects tenant+role from available options
 */
export function useSelectSession() {
  const { pendingToken, selectSession } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, roleId }: { tenantId: string; roleId: string }) => {
      if (!pendingToken) {
        throw new Error('No pending login session');
      }

      const response = await fetch('/api/v1/auth/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pending_token: pendingToken,
          tenant_id: tenantId,
          role_id: roleId,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(getErrorMessage(error) || 'Failed to establish session');
      }

      const data = await response.json() as SessionEstablishResponse;
      return data;
    },
    onSuccess: async (data) => {
      queryClient.clear();
      
      const payload = parseJwtPayload(data.access_token);
      setAuthToken(data.access_token);
      // S49.6: keep the refresh token. Every one of these paths previously typed it and
      // discarded it, so even after /auth/refresh existed there was nothing to present.
      storeRefreshToken(data.refresh_token);

      // Fetch effective permissions
      const permissionsResponse = await fetch('/api/v1/me/effective-permissions', {
        headers: { 'Authorization': `Bearer ${data.access_token}` },
      });
      
      let permissions: string[] = [];
      if (permissionsResponse.ok) {
        const permData = await permissionsResponse.json() as { 
          permissions: Array<{ name: string; source: string }>;
        };
        permissions = permData.permissions.map(p => p.name);
      }

      const session: SessionContext = {
        tenantId: String(payload.tenant_id ?? ''),
        tenantName: String(payload.tenant_name ?? ''),
        tenantSlug: String(payload.tenant_slug ?? ''),
        roleId: String(payload.role_id ?? ''),
        roleName: String(payload.role_name ?? ''),
        roleSlug: String(payload.role_slug ?? ''),
        isPlatformAdmin: Boolean(payload.is_platform_admin),
        accountType: String(payload.account_type ?? 'organization'),  // Sprint 34.15
      };

      selectSession(data.access_token, session, permissions);
      navigate(consumeReturnPath() ?? '/');
    },
  });
}

/**
 * Hook for switching session (already authenticated)
 * 
 * Sprint 31.9 Phase 11: Switch between available tenant+role combinations
 */
export function useSwitchSession() {
  const { switchSession } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, roleId }: { tenantId: string; roleId: string }) => {
      const response = await fetch('/api/v1/auth/switch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantId,
          role_id: roleId,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(getErrorMessage(error) || 'Failed to switch session');
      }

      const data = await response.json() as SessionEstablishResponse;
      return data;
    },
    onSuccess: async (data) => {
      // Clear cached data (new tenant context)
      queryClient.clear();
      
      const payload = parseJwtPayload(data.access_token);
      setAuthToken(data.access_token);
      // S49.6: keep the refresh token. Every one of these paths previously typed it and
      // discarded it, so even after /auth/refresh existed there was nothing to present.
      storeRefreshToken(data.refresh_token);

      // Fetch effective permissions for new session
      const permissionsResponse = await fetch('/api/v1/me/effective-permissions', {
        headers: { 'Authorization': `Bearer ${data.access_token}` },
      });
      
      let permissions: string[] = [];
      if (permissionsResponse.ok) {
        const permData = await permissionsResponse.json() as { 
          permissions: Array<{ name: string; source: string }>;
        };
        permissions = permData.permissions.map(p => p.name);
      }

      const session: SessionContext = {
        tenantId: String(payload.tenant_id ?? ''),
        tenantName: String(payload.tenant_name ?? ''),
        tenantSlug: String(payload.tenant_slug ?? ''),
        roleId: String(payload.role_id ?? ''),
        roleName: String(payload.role_name ?? ''),
        roleSlug: String(payload.role_slug ?? ''),
        isPlatformAdmin: Boolean(payload.is_platform_admin),
        accountType: String(payload.account_type ?? 'organization'),  // Sprint 34.15
      };

      switchSession(data.access_token, session, permissions);
    },
  });
}

/**
 * Hook to fetch available sessions for session switcher
 * 
 * Sprint 31.9 Phase 11: Refreshes available session options
 */
export function useFetchAvailableSessions() {
  const { setAvailableSessions } = useAuthStore();

  return useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/v1/me/sessions');
      if (!response.ok) {
        throw new Error('Failed to fetch sessions');
      }
      const data = await response.json() as { sessions: SessionOption[] };
      return data.sessions;
    },
    onSuccess: (sessions) => {
      setAvailableSessions(sessions);
    },
  });
}

/**
 * Complete login after receiving access token
 * Fetches permissions and builds session context from JWT
 */
async function completeLogin(
  accessToken: string,
  login: (token: string, user: { id: string; email: string; firstName?: string; lastName?: string; permissions: string[] }, session: SessionContext) => void
) {
  const payload = parseJwtPayload(accessToken);
  setAuthToken(accessToken);

  // Fetch effective permissions
  const permissionsResponse = await fetch('/api/v1/me/effective-permissions', {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
  
  let permissions: string[] = [];
  if (permissionsResponse.ok) {
    const permData = await permissionsResponse.json() as { 
      permissions: Array<{ name: string; source: string }>;
    };
    permissions = permData.permissions.map(p => p.name);
  }

  const user = {
    id: String(payload.sub ?? ''),
    email: String(payload.email ?? ''),
    ...(payload.first_name ? { firstName: String(payload.first_name) } : {}),
    ...(payload.last_name ? { lastName: String(payload.last_name) } : {}),
    permissions,
  };

  const session: SessionContext = {
    tenantId: String(payload.tenant_id ?? ''),
    tenantName: String(payload.tenant_name ?? ''),
    tenantSlug: String(payload.tenant_slug ?? ''),
    roleId: String(payload.role_id ?? ''),
    roleName: String(payload.role_name ?? ''),
    roleSlug: String(payload.role_slug ?? ''),
    isPlatformAdmin: Boolean(payload.is_platform_admin),
    accountType: String(payload.account_type ?? 'organization'),  // Sprint 34.15: org-type gating
  };

  login(accessToken, user, session);
}

/**
 * Hook for user logout
 * 
 * Clears auth state, session storage, and query cache.
 * Navigates to login page.
 */
export function useLogout() {
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Logout is client-side only - just clear local state
      // If server-side token invalidation is needed later, add API call here
      return Promise.resolve();
    },
    onSettled: () => {
      // Clear token from session storage
      setAuthToken(null);

      // Clear auth store
      logout();

      // Clear all cached queries
      queryClient.clear();

      // Navigate to login
      navigate('/login');
    },
  });
}

/**
 * ⚠️ `useAuthInit()` AND `setupAuthEventListeners()` WERE REMOVED HERE (S49.1, 2026-08-14).
 *
 * They decoded the JWT, compared `exp`, and dispatched a redirect on 401 -- and **neither was
 * ever called anywhere in the application**. A grep for both names across the whole frontend
 * returned only their own definitions. So the only client-side expiry check in the product was
 * dead code, which is the direct cause of the reported bug: an expired session left the user
 * on the page until a refresh remounted ProtectedRoute and its effect caught the 401.
 *
 * ⚠️ THEY WERE NOT SIMPLY WIRED UP, because wiring them as written would have broken things:
 *
 *   1. `useAuthInit` ran its whole body during RENDER -- calling `logout()` and
 *      `setLoading(false)` outside any effect. Mounting it unchanged sets state during render
 *      and risks a render loop. This may well be why it was never called.
 *   2. `setupAuthEventListeners` redirected with `window.location.href = '/login'` (a full page
 *      reload) while App.tsx's listener uses `navigate()`. Registering both would double-handle
 *      every 401.
 *
 * Replaced by `hooks/useSessionExpiry.ts`, which is effect-based, evaluates continuously rather
 * than once at mount, and is the single owner of the redirect. It also keeps the one genuinely
 * load-bearing line these functions contained -- `setAuthToken(token)`, which syncs the
 * persisted token into sessionStorage where the api-client middleware reads it.
 */
